import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./analisiDrawdownBacktestAndamentoPortafoglio.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";

type ComponentProps = { data: any; data2?: any };

const AnalisiDrawdownBacktestAndamentoPortafoglio: React.FC<ComponentProps> = ({
  data,
  data2,
}) => {
  //** drawdow chart ( second area ) */
  const drawdownData = data.analisi_backtest.analisi_drawdown;
  const formattedData = drawdownData.map((item) => ({
    ...item,
    value: item.value * 1, // Convert to percentage
  }));
  const { minValueDrawdown, maxValueDrawdown, yAxisTicks } = useMemo(() => {
    // const { yAxisTicks } = useMemo(() => {
    const values = formattedData.map((item) => item.value);
    const min = Math.floor(Math.min(...values) / 5) * 5; // Round down to nearest 5
    const max = Math.ceil(Math.max(...values) / 5) * 5; // Round up to nearest 5

    // Generate ticks every 5%
    const ticks = [];
    for (let i = min; i <= max; i += 5) {
      ticks.push(i);
    }

    const valueRange = max - min;
    let tickStep = 10;
    if (valueRange < 50) tickStep = 5;
    if (valueRange < 20) tickStep = 2;
    if (valueRange < 10) tickStep = 1;

    const yAxisTicks = Array.from(
      { length: Math.floor((max - min) / tickStep) + 1 },
      (_, i) => min + i * tickStep,
    );

    return {
      minValueDrawdown: min,
      maxValueDrawdown: max,
      yAxisTicks: yAxisTicks,
    };
  }, [formattedData]);

  const { t, i18n } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  if (!data) {
    return null;
  }

  // Extract required data
  const portfolioData = data.analisi_backtest.simulazione_andamento_portafoglio;

  const downsample = (portfolioData, interval = 50) => {
    return portfolioData.filter((_, index) => index % interval === 0);
  };

  const downsampledData = downsample(portfolioData, 1);

  const maxDrawdownStats =
    data.analisi_backtest.analisi_drawdown_max_drawdown_stats;
  const recoveryTimeStats =
    data.analisi_backtest.analisi_drawdown_recovery_time_stats;

  // Check if there's a drawdown
  const hasDrawdown = maxDrawdownStats && maxDrawdownStats[1]?.weight !== null;

  // Get drawdown dates
  const drawdownStartDate = hasDrawdown
    ? new Date(maxDrawdownStats[1]?.weight)
    : null;
  const drawdownEndDate = hasDrawdown
    ? new Date(maxDrawdownStats[2]?.weight)
    : null;

  const hasRecovery =
    hasDrawdown && recoveryTimeStats && recoveryTimeStats[2]?.weight !== null;

  const recoveryEndDate = hasRecovery
    ? new Date(recoveryTimeStats[2]?.weight)
    : null;

  const processedData = downsampledData.map((point) => {
    const dateObj = new Date(point.date);
    const formattedDate = dateObj.getFullYear();
    return {
      ...point,
      originalDate: point.date,
      date: formattedDate,
      value: parseFloat(point.value),
    };
  });

  // Sort data by date
  const sortedData = [...processedData].sort(
    (a, b) =>
      new Date(a.originalDate).getTime() - new Date(b.originalDate).getTime(),
  );

  // Prepare the unified dataset with nulls for each segment
  const chartData = sortedData.map((point) => {
    const pointDate = new Date(point.originalDate);

    // Initialize all segment values as null
    const dataPoint = {
      ...point,
      normalValue: null,
      drawdownValue: null,
      recoveryValue: null,
      normalAfterValue: null,
    };

    // Determine which segment this point belongs to and set only that value
    if (hasDrawdown) {
      if (pointDate < drawdownStartDate) {
        dataPoint.normalValue = point.value;
      } else if (
        pointDate >= drawdownStartDate &&
        pointDate <= drawdownEndDate
      ) {
        dataPoint.drawdownValue = point.value;
      } else if (
        hasRecovery &&
        pointDate > drawdownEndDate &&
        pointDate <= recoveryEndDate
      ) {
        dataPoint.recoveryValue = point.value;
      } else {
        dataPoint.normalAfterValue = point.value;
      }
    } else {
      // If no drawdown, everything is normal period
      dataPoint.normalValue = point.value;
    }

    return dataPoint;
  });

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Proxima Nova",
  };

  // Calculate Y-axis range
  const allValues = processedData.map((d) => d.value);
  const actualMin = Math.min(...allValues);
  const actualMax = Math.max(...allValues);
  const dataRange = actualMax - actualMin;

  const isSmallRange = dataRange < actualMin * 0.5;
  const bufferPercentage = isSmallRange ? 0.1 : 0.05;

  const minValue = Math.floor(actualMin * (1 - bufferPercentage));
  const maxValue = Math.ceil(actualMax * (1 + bufferPercentage));

  const valueDiff = maxValue - minValue;
  let tickStep = valueDiff < 60 ? 10 : 50;
  tickStep = valueDiff < 40 ? 5 : tickStep;
  tickStep = valueDiff < 20 ? 2 : tickStep;
  tickStep = valueDiff < 10 ? 1 : tickStep;

  // Custom tooltip for handling different segment values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Find the first non-null payload entry
      const validEntry = payload.find((entry) => entry.value !== null);
      if (!validEntry) return null;

      const originalDate = new Date(validEntry.payload.originalDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);

      // Determine which segment is active at this point
      let segmentColor = colorsConfig.FinecoBluePrimary;
      let segmentValue = null;

      if (validEntry.payload.drawdownValue !== null) {
        segmentColor = "#FF0000"; // Red
        segmentValue = validEntry.payload.drawdownValue;
      } else if (validEntry.payload.recoveryValue !== null) {
        segmentColor = "#008000"; // Green
        segmentValue = validEntry.payload.recoveryValue;
      } else if (validEntry.payload.normalValue !== null) {
        segmentValue = validEntry.payload.normalValue;
      } else if (validEntry.payload.normalAfterValue !== null) {
        segmentValue = validEntry.payload.normalAfterValue;
      }

      if (segmentValue === null) return null;

      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          <p
            style={{
              color: segmentColor,
              margin: "3px 0",
              fontSize: "12px",
            }}
          >
            {`${t("portfolio.value")}: ${segmentValue.toFixed(2)}`}
          </p>
        </div>
      );
    }
    return null;
  };
  const CustomTooltipDrawdown = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const segmentValue = payload[0].value;
      const segmentDate = payload[0].payload.date;
      const segmentColor = colorsConfig.FinecoBluePrimary;

      const originalDate = new Date(segmentDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);
      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          <p
            style={{
              color: segmentColor,
              margin: "3px 0",
              fontSize: "12px",
            }}
          >
            {`${t("portfolio.value")}: ${segmentValue.toFixed(2)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -25, bottom: 10 }}
            >
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal={true}
                vertical={false}
              />

              <YAxis
                tick={chartCopy}
                tickCount={Math.ceil(valueDiff / tickStep)}
                domain={[minValue, maxValue]}
                tickLine={false}
                tickFormatter={(value) => Math.round(value).toString()}
                ticks={Array.from(
                  { length: Math.ceil(valueDiff / tickStep) + 1 },
                  (_, i) =>
                    Math.floor(minValue / tickStep) * tickStep + i * tickStep,
                )}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} />} />
              <Line
                type="monotone"
                dataKey={() => 100}
                stroke={colorsConfig.FinecoGrayLight1}
                strokeDasharray="3 3"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />

              <Line
                name={t("portfolio.normalPeriod") || "Normal Period"}
                type="monotone"
                dataKey="normalValue"
                stroke={colorsConfig.FinecoBluePrimary}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Drawdown period */}
              <Line
                name={t("portfolio.drawdownPeriod") || "Drawdown Period"}
                type="monotone"
                dataKey="drawdownValue"
                stroke={colorsConfig.red}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Recovery period */}
              <Line
                name={t("portfolio.recoveryPeriod") || "Recovery Period"}
                type="monotone"
                dataKey="recoveryValue"
                stroke={colorsConfig.Green}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Normal period after recovery */}
              <Line
                name={t("portfolio.normalPeriod") || "Normal Period"}
                type="monotone"
                dataKey="normalAfterValue"
                stroke={colorsConfig.FinecoBluePrimary}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <AreaChart
              data={formattedData}
              margin={{ top: 20, right: 10, left: -25, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="rgb(255, 255, 255)"
                    stopOpacity={1}
                  />
                  <stop
                    offset="80%"
                    stopColor={colorsConfig.BlueLight}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                dx={10}
                dy={10}
                textAnchor="end"
                // height={60}
                interval={250}
                tick={chartCopy}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("it-IT", {
                    year: "numeric",
                  })
                }
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />
              <YAxis
                tick={chartCopy}
                tickFormatter={(value) => `${value}`}
                domain={[minValueDrawdown, maxValueDrawdown]}
                ticks={yAxisTicks}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltipDrawdown />} />
              {/* <Legend
              content={<CustomizedLegend />}
              verticalAlign="bottom"
              height={0}
            /> */}
              <Area
                type="monotone"
                dataKey="value"
                stroke={colorsConfig.BlueLight}
                fill="url(#gradientFill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalisiDrawdownBacktestAndamentoPortafoglio;
